import { useEffect } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { SelectGiftCard } from "./SelectGiftCard";
import { CheckoutGiftCard } from "./CheckoutGiftCard";
import { PurchaseGiftSuccessModal } from "./PurchaseGiftSuccessModal";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import { useAppStore } from "@/global/store";
import {
  selectGiftCards,
  purchaseGiftCards,
} from "@/global/store/data/giftCards/purchaseGiftCards";
import { parseApiErrorMessage } from "@/global/utils/utils";
import { usePurchaseGiftCard } from "./usePurchaseGiftCard";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useDeliveryMethodDatePicker } from "./DeliveryMethodSelector/DeliveryMethodDatePicker";
import { useDeliveryMethodSelectorState } from "./DeliveryMethodSelector/DeliveryMethodSelector";

export interface PurchaseGiftCardProps {
  isEmbeddedCheckout: boolean;
  authRedirectPath: string;
  skipRegister?: boolean;
}

export function PurchaseGiftCard(props: PurchaseGiftCardProps) {
  const dispatch = useDispatch();
  const { md: mdDown } = useCssMediaQuery("down");
  const giftCards = useAppStore(selectGiftCards);
  const { apis, states, handlers } = usePurchaseGiftCard(props);
  const { getGiftCards } = apis;
  const { t } = useTranslation();
  const { set: setDeliveryDate } = useDeliveryMethodDatePicker();
  const { set: setDeliveryMethod } = useDeliveryMethodSelectorState();
  const { giftCardConfigs } = useTeamConfig();
  const enableConfirmationModal = giftCardConfigs?.enableConfirmationModal || false; // prettier-ignore

  useEffect(() => {
    // fetching gift cards
    handlers.handleGetGiftCards((giftCards) => {
      dispatch(purchaseGiftCards.actions.setGiftCards(giftCards));
      // handling auth redirect
      handlers.handleAuthRedirect(giftCards);
    });
  }, []);

  return (
    <>
      <PurchaseGiftSuccessModal
        isOpen={states.checkoutSuccessModal.open}
        onClose={handlers.handleSuccessModalClose}
        purchaseResponse={states.purchaseResponse}
        recipientForm={states.recipientForm}
        deliveryDate={states.deliveryDate}
      />
      {/* checkout error modal */}
      <NotificationModal
        isOpen={states.checkoutErrorModal.open}
        onClose={() => {
          handlers.handleRemoveUrlPayload();
          states.checkoutErrorModal.onClose();
        }}
        onClickPrimaryButton={() => {
          handlers.handleRemoveUrlPayload();
          states.checkoutErrorModal.onClose();
        }}
        title={t("giftCard.checkout.errorModal.title") || "Error"}
        description={
          <Box minWidth={mdDown ? "100%" : "550px"}>
            {parseApiErrorMessage(apis.checkoutGiftCard.error)}
          </Box>
        }
        hideSecondaryButton
        primaryButtonText={
          t("giftCard.checkout.errorModal.closeButtonText") || "Close"
        }
      />

      {!(props.isEmbeddedCheckout && states.isCheckoutOpen) ? (
        <SelectGiftCard
          isGiftCardsLoading={!getGiftCards.isSuccess || getGiftCards.isLoading}
          isAuthLoading={states.isAuthLoading}
          giftCards={giftCards}
          selectedGiftCard={states.selectedGiftCard}
          ref={states.recipientFormSubmitRef}
          recipientForm={states.recipientForm}
          onSelectGiftCard={handlers.handleSelectGiftCard}
          onSubmitRecipientForm={handlers.handleProceedCheckout}
          onClickCheckout={handlers.handleProceedCheckout}
          tempUser={states.tempUser}
        />
      ) : null}

      {states.isCheckoutOpen ? (
        <CheckoutGiftCard
          isLoading={false}
          isEmbeddedCheckout={props.isEmbeddedCheckout}
          isCheckoutLoading={
            apis.checkoutGiftCard.isLoading ||
            apis.findAndRetrieveEkataScore.isLoading ||
            apis.getGiftCardSalesByAccountId.isLoading
          }
          tempUser={states.tempUser}
          recipientForm={states.recipientForm}
          selectedGiftCard={states.selectedGiftCard}
          onCloseTab={() => {
            handlers.handleRemoveUrlPayload();
            handlers.setIsCheckoutOpen(false);
            setDeliveryMethod({ selectedDeliveryMethod: "sendNow" });
            setDeliveryDate({ deliveryDate: null, isError: false });
          }}
          onSubmitCheckout={
            enableConfirmationModal
              ? handlers.handleOpenConfirmationModal
              : handlers.handleCheckout
          }
          onSubmitRecipientForm={handlers.handleClickCheckout}
          recipientFormSubmitRef={states.recipientFormSubmitRef}
        />
      ) : null}
    </>
  );
}
